import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import logo from './Designer.png';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const { currentUser, logout, isAuthenticated } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    // Detect scroll position for navbar styling
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 10) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };
    
    // Helper function to check active route
    const isActive = (path) => {
        return location.pathname === path;
    };

    // Handle logout
    const handleLogout = () => {
        logout();
        navigate('/login');
        setIsOpen(false); // Close mobile menu if open
    };

    // Get user role or default to 'patient'
    const userRole = currentUser?.role || 'patient';
    
    // Get dashboard URL based on role
    const getDashboardUrl = () => {
        return userRole === 'doctor' ? '/doctor-dashboard' : '/dashboard';
    };

    return (
        <>
            <nav className={`fixed w-full text-white top-0 z-50 transition-all duration-300 ${
                isScrolled ? 'bg-blue-900/95 shadow-lg backdrop-blur-sm py-2' : 'bg-blue-900 py-4'
            }`}>
                <div className="container mx-auto flex justify-between items-center px-4">
                    <Link to="/" className="text-2xl font-bold flex items-center group">
                        <img 
                            src={logo} 
                            alt="SeraphMed Logo" 
                            className="h-9 mr-2 transform group-hover:scale-105 transition-transform duration-300" 
                        />
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-200">
                            SeraphMed
                        </span>
                    </Link>
                    
                    {/* Desktop Navigation */}
                    <ul className="hidden md:flex items-center space-x-2 lg:space-x-4">
                        {!isAuthenticated ? (
                            <>
                                <li>
                                    <a 
                                        href="#features" 
                                        className="px-3 py-2 rounded-md hover:bg-blue-800 hover:text-white transition-colors duration-200"
                                    >
                                        Features
                                    </a>
                                </li>
                                <li className="relative">
                                <a
                        href="#services"
                        className="px-3 py-2 rounded-md hover:bg-blue-800 hover:text-white transition-colors duration-200"
                        >
                                        Services
                                        {/* <svg className={`w-4 h-4 ml-1 transform transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                        </svg> */}
                                    </a>
                                    {/* {isDropdownOpen && (
                                        <ul className="absolute right-0 mt-1 w-56 bg-white text-blue-800 rounded-md shadow-lg py-1 animate-fadeIn">
                                            <li>
                                                <Link to="#telemedicine" className="block px-4 py-2 hover:bg-blue-50 transition-colors duration-150">
                                                    Telemedicine
                                                </Link>
                                            </li>
                                            <li>
                                                <Link to="#record-management" className="block px-4 py-2 hover:bg-blue-50 transition-colors duration-150">
                                                    Health Record Management
                                                </Link>
                                            </li>
                                            <li>
                                                <Link to="#support" className="block px-4 py-2 hover:bg-blue-50 transition-colors duration-150">
                                                    24/7 Support
                                                </Link>
                                            </li>
                                        </ul>
                                    )} */}
                                </li>
                                <li>
                                    <a 
                                        href="/register" 
                                        className="px-3 py-2 rounded-md hover:bg-blue-800 hover:text-white transition-colors duration-200"
                                    >
                                        Book Appointment
                                    </a>
                                </li>
                                <li className="ml-2">
                                    <Link 
                                        to="/login" 
                                        className="px-4 py-2 border border-blue-300 rounded-md hover:bg-blue-800 hover:border-blue-200 transition-colors duration-200"
                                    >
                                        Login
                                    </Link>
                                </li>
                                <li>
                                    <Link 
                                        to="/register" 
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-500 transition-colors duration-200"
                                    >
                                        Sign Up
                                    </Link>
                                </li>
                            </>
                        ) : (
                            <>
                            {/* Main Navigation Group */}
                            <div className="flex items-center mr-6 border-r border-blue-700/50 pr-6">
                                <li>
                                    <Link 
                                        to={getDashboardUrl()}
                                        className={`px-3 py-2 rounded-md transition-all duration-200 flex items-center ${
                                            isActive(getDashboardUrl()) 
                                                ? 'bg-blue-700 text-white shadow-md' 
                                                : 'hover:bg-blue-800/70 hover:text-white'
                                        }`}
                                    >
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                        </svg>
                                        <span className="font-medium">Dashboard</span>
                                    </Link>
                                </li>
                                <li className="ml-2">
                                    <Link 
                                        to="/health-records" 
                                        className={`px-3 py-2 rounded-md transition-all duration-200 flex items-center ${
                                            isActive('/health-records') 
                                                ? 'bg-blue-700 text-white shadow-md' 
                                                : 'hover:bg-blue-800/70 hover:text-white'
                                        }`}
                                    >
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <span className="font-medium">Health Records</span>
                                    </Link>
                                </li>
                            </div>
                                            
                            {/* Appointment Group */}
                            <div className="flex items-center border-r border-blue-700/50 pr-6 mr-6">
                                <li>
                                    <Link 
                                        to="/appointments" 
                                        className={`px-3 py-2 rounded-md transition-all duration-200 relative flex items-center ${
                                            isActive('/appointments') 
                                                ? 'bg-blue-700 text-white shadow-md' 
                                                : 'hover:bg-blue-800/70 hover:text-white'
                                        }`}
                                    >
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <span className="font-medium">Appointments</span>
                                       
                                    </Link>
                                </li>
                                {userRole === 'doctor' && (
                                    <li className="ml-2">
                                        <Link 
                                            to="/patients" 
                                            className={`px-3 py-2 rounded-md transition-all duration-200 flex items-center ${
                                                isActive('/patients') 
                                                    ? 'bg-blue-700 text-white shadow-md' 
                                                    : 'hover:bg-blue-800/70 hover:text-white'
                                            }`}
                                        >
                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                            </svg>
                                            <span className="font-medium">Patients</span>
                                        </Link>
                                    </li>
                                )}
                                {userRole === 'patient' && (
                                    <li className="ml-2">
                                        <Link 
                                            to="/predictions" 
                                            className={`px-3 py-2 rounded-md transition-all duration-200 flex items-center ${
                                                isActive('/predictions') 
                                                    ? 'bg-blue-700 text-white shadow-md' 
                                                    : 'hover:bg-blue-800/70 hover:text-white'
                                            }`}
                                        >
                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                            </svg>
                                            <span className="font-medium">Predictions</span>
                                        </Link>
                                    </li>
                                )}
                            </div>
                                            
                            {/* User Account Group */}
                            <div className="flex items-center">
                                <li className="ml-2">
                                    <Link 
                                        to="/profile" 
                                        className={`flex items-center px-3 py-2 rounded-md transition-all duration-200 ${
                                            isActive('/profile') 
                                                ? 'bg-blue-700 text-white shadow-md' 
                                                : 'hover:bg-blue-800/70 hover:text-white'
                                        }`}
                                    >
                                        <span className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center mr-2 shadow-md">
                                            {currentUser && currentUser.firstName ? (
                                                <span className="text-white font-medium">
                                                    {currentUser.firstName.charAt(0)}{currentUser.lastName?.charAt(0)}
                                                </span>
                                            ) : (
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                            )}
                                        </span>
                                        <span className="font-medium">Profile</span>
                                    </Link>
                                </li>
                                <li>
                                    <button 
                                        onClick={handleLogout}
                                        className="ml-3 px-4 py-2 border border-blue-300/70 rounded-md hover:bg-blue-800 hover:border-blue-200 transition-all duration-200 hover:shadow-md"
                                    >
                                        <span className="font-medium">Logout</span>
                                    </button>
                                </li>
                            </div>
                        </>
                        )}
                    </ul>
                    
                    {/* Mobile Menu Button */}
                    <button 
                        onClick={toggleMenu} 
                        className="md:hidden focus:outline-none flex flex-col items-center justify-center"
                        aria-label="Toggle menu"
                    >
                        <span className={`block w-6 h-0.5 bg-white mb-1.5 transition-transform duration-300 ease-in-out ${isOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                        <span className={`block w-6 h-0.5 bg-white mb-1.5 transition-opacity duration-300 ${isOpen ? 'opacity-0' : 'opacity-100'}`}></span>
                        <span className={`block w-6 h-0.5 bg-white transition-transform duration-300 ease-in-out ${isOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
                    </button>
                </div>
                
                {/* Mobile Menu - Update logout link to button */}
                <div className={`transform transition-all duration-300 ease-in-out md:hidden overflow-hidden ${isOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
                    <ul className="bg-blue-800 shadow-inner px-4 py-2">
                        {!isAuthenticated ? (
                            <>
                              <div className="mb-4">
        <h3 className="text-blue-300 text-xs uppercase tracking-wider py-2">Menu</h3>
        <li>
            <a 
                href="#features" 
                className="flex items-center py-3 border-b border-blue-700 hover:text-blue-200 transition-colors"
                onClick={() => setIsOpen(false)}
            >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                Features
            </a>
        </li>
        <li>
            <a 
                href="#services" 
                className="flex items-center py-3 border-b border-blue-700 hover:text-blue-200 transition-colors"
                onClick={() => setIsOpen(false)}
            >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                Services
            </a>
        </li>
        <li>
            <a 
                href="/register" 
                className="flex items-center py-3 border-b border-blue-700 hover:text-blue-200 transition-colors"
                onClick={() => setIsOpen(false)}
            >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Book Appointment
            </a>
        </li>
    </div>
    
    <div>
        <h3 className="text-blue-300 text-xs uppercase tracking-wider py-2">Account</h3>
        <li>
            <Link 
                to="/login" 
                className="flex items-center py-3 border-b border-blue-700 hover:text-blue-200 transition-colors"
                onClick={() => setIsOpen(false)}
            >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                Login
            </Link>
        </li>
        <li>
            <Link 
                to="/register"
                className="flex items-center py-3 text-blue-200 font-medium hover:text-white transition-colors"
                onClick={() => setIsOpen(false)}
            >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                Sign Up
            </Link>
        </li>
    </div>
                            </>
                        ) : (
                            <>
                                {/* Update mobile navigation for authenticated users */}
                                <div className="mb-4">
                                    <h3 className="text-blue-300 text-xs uppercase tracking-wider py-2">Main</h3>
                                    <li>
                                        <Link 
                                            to={getDashboardUrl()}
                                            className={`flex items-center py-3 border-b border-blue-700 hover:text-blue-200 transition-colors ${isActive(getDashboardUrl()) ? 'text-white font-medium' : ''}`}
                                            onClick={() => setIsOpen(false)}
                                        >
                                            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                            </svg>
                                            Dashboard
                                        </Link>
                                    </li>
                                    <li>
                                        <Link 
                                            to="/health-records" 
                                            className={`flex items-center py-3 border-b border-blue-700 hover:text-blue-200 transition-colors ${isActive('/health-records') ? 'text-white font-medium' : ''}`}
                                            onClick={() => setIsOpen(false)}
                                        >
                                            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            Health Records
                                        </Link>
                                    </li>
                                </div>
                                
                                <div className="mb-4">
                                    <h3 className="text-blue-300 text-xs uppercase tracking-wider py-2">Appointments</h3>
                                    <li>
                                        <Link 
                                            to="/appointments" 
                                            className={`flex items-center justify-between py-3 border-b border-blue-700 hover:text-blue-200 transition-colors ${isActive('/appointments') ? 'text-white font-medium' : ''}`}
                                            onClick={() => setIsOpen(false)}
                                        >
                                            <div className="flex items-center">
                                                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                                Appointments
                                            </div>
                                           
                                        </Link>
                                    </li>
                                    {userRole === 'doctor' && (
                                        <li>
                                            <Link 
                                                to="/patients" 
                                                className={`flex items-center py-3 border-b border-blue-700 hover:text-blue-200 transition-colors ${isActive('/patients') ? 'text-white font-medium' : ''}`}
                                                onClick={() => setIsOpen(false)}
                                            >
                                                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                </svg>
                                                Patients
                                            </Link>
                                        </li>
                                    )}
                                    {userRole === 'patient' && (
                                        <li>
                                            <Link 
                                                to="/predictions" 
                                                className={`flex items-center py-3 border-b border-blue-700 hover:text-blue-200 transition-colors ${isActive('/predictions') ? 'text-white font-medium' : ''}`}
                                                onClick={() => setIsOpen(false)}
                                            >
                                                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                </svg>
                                                Predictions
                                            </Link>
                                        </li>
                                    )}
                                </div>
                                
                                <div>
                                    <h3 className="text-blue-300 text-xs uppercase tracking-wider py-2">Account</h3>
                                    <li>
                                        <Link 
                                            to="/profile" 
                                            className={`flex items-center py-3 border-b border-blue-700 hover:text-blue-200 transition-colors ${isActive('/profile') ? 'text-white font-medium' : ''}`}
                                            onClick={() => setIsOpen(false)}
                                        >
                                            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                            Profile
                                        </Link>
                                    </li>
                                    <li>
                                        <button 
                                            onClick={handleLogout}
                                            className="flex items-center py-3 w-full text-left hover:text-blue-200 transition-colors"
                                        >
                                            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                            </svg>
                                            Logout
                                        </button>
                                    </li>
                                </div>
                            </>
                        )}
                    </ul>
                </div>
            </nav>
        </>
    );
};

export default Navbar;